class FaqSection extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('.faq-search-input');
    this.collapses = this.querySelectorAll('theme-collapse');
    this.noResultsMessage = null;
    this.srAnnouncement = null;
    this.srAnnouncementTimeout = null;
    this.hasSearched = false;

    if (this.searchInput) {
      this.initializeSearch();
      this.checkUrlParams();
    }
  }

  initializeSearch() {
    this.searchInput.addEventListener('input', this.debounce(this.handleSearch.bind(this), 300));
    
    // Create no results message element
    this.createNoResultsMessage();
    
    // Create screen reader announcement element
    this.createSrAnnouncement();
  }

  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    
    if (query) {
      // Set the input value and trigger search
      this.searchInput.value = query;
      this.searchInput.dispatchEvent(new Event('input'));
      
      // Scroll to the FAQ section
      setTimeout(() => {
        // this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  updateUrl(searchTerm) {
    const url = new URL(window.location);
    
    if (searchTerm) {
      url.searchParams.set('q', searchTerm);
    } else {
      url.searchParams.delete('q');
    }
    
    // Update URL without reloading the page
    window.history.pushState({}, '', url);
  }

  createNoResultsMessage() {
    const noResultsText = this.dataset.textNoResults || 'No FAQs found matching your search. Please try different keywords.';
    
    this.noResultsMessage = document.createElement('div');
    this.noResultsMessage.classList.add('faq-no-results', 'alert');
    this.noResultsMessage.setAttribute('role', 'alert');
    this.noResultsMessage.setAttribute('data-alert-type', 'warning');
    this.noResultsMessage.style.display = 'none';
    this.noResultsMessage.innerHTML = `
      <p class="text-center">${noResultsText}</p>
    `;
    
    const container = this.querySelector('.container');
    if (container) {
      container.appendChild(this.noResultsMessage);
    }
  }

  createSrAnnouncement() {
    this.srAnnouncement = document.createElement('div');
    this.srAnnouncement.classList.add('visually-hidden');
    this.srAnnouncement.setAttribute('role', 'status');
    this.srAnnouncement.setAttribute('aria-live', 'polite');
    this.srAnnouncement.setAttribute('aria-atomic', 'true');
    
    const container = this.querySelector('.container');
    if (container) {
      container.appendChild(this.srAnnouncement);
    }
  }

  announceSrResults(count, searchTerms) {
    if (!this.srAnnouncement) return;
    
    // Clear any existing announcement timeout
    if (this.srAnnouncementTimeout) {
      clearTimeout(this.srAnnouncementTimeout);
    }
    
    // Delay announcement to avoid interrupting while user is typing
    this.srAnnouncementTimeout = setTimeout(() => {
      let message = '';
      if (count === 0) {
        const noResultsText = this.dataset.textNoResults || 'No FAQs found matching your search.';
        message = noResultsText.replace('{{ terms }}', searchTerms);
      } else {
        const template = count === 1 
          ? this.dataset.textResultsOne || '{{ count }} FAQ item found'
          : this.dataset.textResultsOther || '{{ count }} FAQ items found';
        message = template.replace('{{ count }}', count);
      }
      
      this.srAnnouncement.textContent = message;
    }, 1000);
  }

  handleSearch(event) {
    const query = event.target.value.toLowerCase().trim();
    const searchTerms = event.target.value.trim();
    
    // Update URL with search query
    this.updateUrl(searchTerms);
    
    // Remove animations on first search
    if (!this.hasSearched && query) {
      this.removeAnimations();
      this.hasSearched = true;
    }
    
    if (!query) {
      // Show all items when search is empty
      this.showAllItems();
      return;
    }

    let visibleCount = 0;

    this.collapses.forEach((collapse) => {
      const title = collapse.querySelector('summary span')?.textContent.toLowerCase() || '';
      const content = collapse.querySelector('.theme-collapse-content-inner')?.textContent.toLowerCase() || '';

      // Check if query matches title or content
      if (title.includes(query) || content.includes(query)) {
        collapse.style.display = '';
        collapse.removeAttribute('hidden');
        visibleCount++;
        
        // Optionally highlight matching text
        if (title.includes(query)) {
          collapse.classList.add('faq-search-match');
        }
      } else {
        collapse.style.display = 'none';
        collapse.setAttribute('hidden', 'hidden');
        collapse.classList.remove('faq-search-match');
      }
    });

    // Show/hide no results message
    if (visibleCount === 0) {
      const noResultsText = this.dataset.textNoResults || 'No FAQs found matching your search. Please try different keywords.';
      this.noResultsMessage.querySelector('p').textContent = noResultsText.replace('{{ terms }}', searchTerms);
      this.noResultsMessage.style.display = 'block';
    } else {
      this.noResultsMessage.style.display = 'none';
    }

    // Announce results to screen readers
    this.announceSrResults(visibleCount, searchTerms);

    // Also handle faq headers
    this.handleHeaders(query);
  }

  handleHeaders(query) {
    const headers = this.querySelectorAll('.faq-header');
    
    headers.forEach((header) => {
      let nextElement = header.nextElementSibling;
      let hasVisibleCollapses = false;

      // Check if any following collapses (before next header) are visible
      while (nextElement && !nextElement.classList.contains('faq-header')) {
        if (nextElement.tagName === 'THEME-COLLAPSE' && nextElement.style.display !== 'none') {
          hasVisibleCollapses = true;
          break;
        }
        nextElement = nextElement.nextElementSibling;
      }

      // Hide header if no visible collapses follow it
      if (query && !hasVisibleCollapses) {
        header.style.display = 'none';
      } else {
        header.style.display = '';
      }
    });
  }

  showAllItems() {
    this.collapses.forEach((collapse) => {
      collapse.style.display = '';
      collapse.removeAttribute('hidden');
      collapse.classList.remove('faq-search-match');
    });

    const headers = this.querySelectorAll('.faq-header');
    headers.forEach((header) => {
      header.style.display = '';
    });

    if (this.noResultsMessage) {
      this.noResultsMessage.style.display = 'none';
    }
    
    // Clear screen reader announcement and timeout
    if (this.srAnnouncementTimeout) {
      clearTimeout(this.srAnnouncementTimeout);
    }
    if (this.srAnnouncement) {
      this.srAnnouncement.textContent = '';
    }
  }

  removeAnimations() {
    // Remove scroll animations from all FAQ elements
    this.collapses.forEach((collapse) => {
      collapse.classList.remove('scroll-animate-fade-in-up', 'scroll-animate-fade-in', 'scroll-animate');
      collapse.style.animationDelay = '';
    });

    const headers = this.querySelectorAll('.faq-header');
    headers.forEach((header) => {
      header.classList.remove('scroll-animate-fade-in-up', 'scroll-animate-fade-in', 'scroll-animate');
      header.style.animationDelay = '';
    });

    const sectionHeader = this.querySelector('.section-header');
    if (sectionHeader) {
      sectionHeader.classList.remove('scroll-animate-fade-in-up', 'scroll-animate-fade-in', 'scroll-animate');
      sectionHeader.style.animationDelay = '';
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('faq-section', FaqSection);
